<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { SHARED } from "@/constants";
import { mapState } from "vuex";
import { DELIVERY_MODULE } from "@/state/modules/delivery";
import { LOOKUP_MODULE } from "@/state/modules/lookup";
import { SALES_DOC_MODULE } from "@/state/modules/salesDoc";
import { deliveryMethods, lookupMethods, salesDocMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

export default {
  page: { title: i18n.t("label.listingX", [i18n.t("module.delivery")]) },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      title: i18n.t("label.listingX", [i18n.t("module.delivery")]),
      currentPage: 1,
      perPage: SHARED.DEFAULT_PAGE_SIZE,
      filter: "",
      activeType: "outbound",
      searchCriteria: {},

      // Create modal
      newForm: {
        salesDocNo: "",
        docType: "salesorder",
        debtorCode: "",
        deliveryAddress: "",
        notes: "",
        autoCreateDo: true,
      },
      selectedDebtor: null,
      selectedDocType: null,
      selectedSalesDoc: null,
      docLoading: false,
      creating: false,
      // Only these doc types make sense as delivery sources.
      ALLOWED_SOURCE_TYPES: ["salesorder", "salesinvoice", "consignment"],
    };
  },
  computed: {
    ...mapState(DELIVERY_MODULE, ["status"]),
    ...mapState(LOOKUP_MODULE, ["debtorDdl", "salesDocTypeDdl", "salesDocLookup"]),
    ...mapState(SALES_DOC_MODULE, { pickedDoc: "details" }),
    pickedDocLines() {
      return (this.pickedDoc && (this.pickedDoc.Details || this.pickedDoc.details)) || [];
    },
    debtorNameByCode() {
      const map = {};
      (this.debtorDdl || []).forEach((d) => { if (d.account_no) map[d.account_no] = d.company_name || ""; });
      return map;
    },
    tableRows() {
      const rows = this.$store.state.delivery.list.data || [];
      return rows.map((r) => ({ ...r, debtorName: this.debtorNameByCode[r.debtorCode] || "" }));
    },
    pagination() { return this.$store.state.delivery.list.pagination; },
    sourceDocTypeOptions() {
      // Only SO / SI / Consignment are valid delivery sources.
      return (this.salesDocTypeDdl || []).filter((t) => this.ALLOWED_SOURCE_TYPES.includes(t.value));
    },
    canSubmitNew() {
      return !!this.newForm.salesDocNo && !!this.newForm.debtorCode && !!this.newForm.docType;
    },
    fields() {
      const common = [
        { key: "autocountDocNo", label: this.activeType === "return" ? i18n.t("delivery.drDocNo") : i18n.t("delivery.doDocNo") },
        { key: "salesDocNo", label: i18n.t("delivery.sourceDoc") },
        { key: "debtorCode", label: i18n.t("label.debtorCode") },
        { key: "debtorName", label: i18n.t("label.debtorName") },
        { key: "status", label: i18n.t("label.status") },
        { key: "createdAt", label: i18n.t("delivery.created") },
        { key: "action", label: i18n.t("label.action") },
      ];
      if (this.activeType === "outbound") {
        common.splice(5, 0,
          { key: "dispatchAt", label: i18n.t("delivery.dispatched") },
        );
      }
      return common;
    },
  },
  async created() {
    this.searchCriteria = { pageNumber: 1, pageSize: SHARED.DEFAULT_PAGE_SIZE, searchArray: [], type: this.activeType };
    await Promise.all([
      this.fetch(),
      this.lookupDebtor({}),
      this.lookupSalesDocType(),
    ]);
  },
  methods: {
    ...deliveryMethods,
    ...lookupMethods,
    ...salesDocMethods,
    fetch() { this.getAllDeliveries({ filter: this.searchCriteria }); },
    pageNavigation(p) { this.searchCriteria.pageNumber = p; this.fetch(); },
    searchListing() {
      this.searchCriteria.pageNumber = 1;
      this.searchCriteria.searchArray = [this.filter];
      this.fetch();
    },
    onTypeChange(type) {
      this.activeType = type;
      this.searchCriteria.type = type;
      this.searchCriteria.pageNumber = 1;
      this.fetch();
    },
    goEdit(row) { this.$router.push({ name: "Delivery-update", params: { id: row.id } }); },
    statusVariant(s) {
      return { pending: "secondary", dispatched: "info", delivered: "success", returned: "warning", cancelled: "danger" }[s] || "secondary";
    },
    openCreate() {
      this.newForm = {
        salesDocNo: "",
        docType: "salesorder",
        debtorCode: "",
        deliveryAddress: "",
        notes: "",
        autoCreateDo: true,
      };
      this.selectedDebtor = null;
      this.selectedSalesDoc = null;
      this.selectedDocType = this.sourceDocTypeOptions.find((t) => t.value === "salesorder") || this.sourceDocTypeOptions[0] || null;
      // Keep newForm.docType in sync with whatever is actually selected.
      this.newForm.docType = this.selectedDocType ? this.selectedDocType.value : "";
      // Reset preview doc.
      this.$store.state.salesDoc.details = {};
      // Preload sales doc list for default doc type.
      if (this.selectedDocType) this.lookupSalesDoc({ type: this.selectedDocType.value });
      this.$bvModal.show("createDeliveryModal");
    },
    onPickDocType(t) {
      this.selectedDocType = t;
      this.newForm.docType = t ? t.value : "";
      // Doc type changed — clear current sales doc pick and reload list.
      this.selectedSalesDoc = null;
      this.newForm.salesDocNo = "";
      this.selectedDebtor = null;
      this.newForm.debtorCode = "";
      this.$store.state.salesDoc.details = {};
      if (t) this.lookupSalesDoc({ type: t.value });
    },
    async onPickSalesDoc(d) {
      this.selectedSalesDoc = d;
      this.newForm.salesDocNo = d ? d.doc_no : "";
      if (!d) {
        this.selectedDebtor = null;
        this.newForm.debtorCode = "";
        this.$store.state.salesDoc.details = {};
        return;
      }
      // Auto-fill debtor from picked sales doc. Address URL is user-entered, do not auto-fill.
      if (d.debtor_code) {
        const debtor = (this.debtorDdl || []).find((b) => b.account_no === d.debtor_code);
        if (debtor) {
          this.selectedDebtor = debtor;
          this.newForm.debtorCode = debtor.account_no;
        } else {
          this.selectedDebtor = { account_no: d.debtor_code, company_name: d.company_name };
          this.newForm.debtorCode = d.debtor_code;
        }
      }
      // Fetch full doc to preview lines.
      this.docLoading = true;
      try {
        await this.getSalesDoc({ docType: this.newForm.docType, docNo: d.doc_no });
      } finally {
        this.docLoading = false;
      }
    },
    async submitCreate() {
      if (!this.canSubmitNew) return;
      this.creating = true;
      try {
        const r = await this.createDelivery({
          data: {
            sales_doc_no: this.newForm.salesDocNo,
            doc_type: this.newForm.docType,
            debtor_code: this.newForm.debtorCode,
            delivery_address: this.newForm.deliveryAddress,
            notes: this.newForm.notes,
            auto_create_do: this.newForm.autoCreateDo,
          },
          filter: this.searchCriteria,
        });
        if (r) {
          this.$bvModal.hide("createDeliveryModal");
          this.fetch();
        }
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-nav tabs class="mb-3">
              <b-nav-item :active="activeType === 'outbound'" @click="onTypeChange('outbound')">
                <i class="mdi mdi-truck-delivery-outline mr-1"></i>{{ $t('delivery.outboundTab') }}
              </b-nav-item>
              <b-nav-item :active="activeType === 'return'" @click="onTypeChange('return')">
                <i class="mdi mdi-truck-fast-outline mr-1 mdi-flip-h"></i>{{ $t('delivery.returnsTab') }}
              </b-nav-item>
            </b-nav>

            <div class="row mt-2 mb-3 align-items-center">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center mb-0">
                  {{ $t('label.search') }}
                  <b-form-input v-model="filter" type="search" class="form-control form-control-sm ml-2" @change="searchListing"></b-form-input>
                </label>
              </div>
              <div class="col-sm-12 col-md-6 text-md-right">
                <b-button v-if="activeType === 'outbound' && $can('Create Delivery')" variant="primary" size="sm" @click="openCreate">
                  <i class="mdi mdi-plus mr-1"></i>{{ $t('delivery.newOutbound') }}
                </b-button>
                <small v-else class="text-muted">
                  <i class="mdi mdi-information-outline mr-1"></i>
                  {{ $t('delivery.returnsNote') }}
                </small>
              </div>
            </div>
            <div class="table-responsive mb-0">
              <b-table :items="tableRows" :fields="fields" responsive="sm" :per-page="perPage" small>
                <template #cell(autocountDocNo)="data">
                  <b-badge variant="light" class="font-monospace">{{ data.item.autocountDocNo || '—' }}</b-badge>
                </template>
                <template #cell(debtorCode)="data">
                  <b-badge variant="light" class="font-monospace">{{ data.item.debtorCode }}</b-badge>
                </template>
                <template #cell(status)="data">
                  <b-badge :variant="statusVariant(data.item.status)">{{ data.item.status }}</b-badge>
                </template>
                <template #cell(action)="data">
                  <a href="javascript:void(0);" @click="goEdit(data.item)" class="text-info" v-b-tooltip.hover.left :title="$t('label.view')">
                    <i class="mdi mdi-eye-outline font-size-18"></i>
                  </a>
                </template>
              </b-table>
            </div>
            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="pagination.total" :per-page="perPage" @change="pageNavigation"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="createDeliveryModal" :title="$t('delivery.newOutbound')" size="lg" hide-footer>
      <b-form @submit.prevent="submitCreate">
        <b-row>
          <b-col cols="12">
            <b-form-group :label="$t('delivery.sourceDocType')" :description="$t('delivery.sourceDocTypeDescription')">
              <multiselect
                :value="selectedDocType"
                :options="sourceDocTypeOptions"
                track-by="value"
                label="name"
                :show-labels="false"
                :allow-empty="false"
                :placeholder="$t('label.select')"
                @input="onPickDocType"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group :label="$t('delivery.sourceSalesDoc')" :description="$t('delivery.sourceSalesDocDescription')">
              <multiselect
                :value="selectedSalesDoc"
                :options="salesDocLookup"
                :custom-label="(o) => `${o.doc_no} — ${o.company_name || o.debtor_code || ''} (${o.doc_date || ''})`"
                track-by="doc_no"
                :show-labels="false"
                :allow-empty="false"
                :disabled="!selectedDocType"
                :placeholder="$t('delivery.pickSalesDoc')"
                @input="onPickSalesDoc"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group :label="$t('label.debtorCode')" :description="$t('delivery.debtorAutoFillDescription')">
              <div v-if="selectedDebtor" class="form-control bg-light d-flex align-items-center" style="min-height: 38px;">
                <b-badge variant="primary" class="font-monospace mr-2">{{ selectedDebtor.account_no }}</b-badge>
                <span>{{ selectedDebtor.company_name || '—' }}</span>
              </div>
              <div v-else class="form-control bg-light text-muted d-flex align-items-center" style="min-height: 38px;">
                {{ $t('delivery.pickSalesDocFirst') }}
              </div>
            </b-form-group>
          </b-col>
          <b-col cols="12" v-if="selectedSalesDoc">
            <div class="card border mb-3">
              <div class="card-body py-2 px-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <strong class="text-primary">{{ $t('delivery.sourceDocPreview') }}</strong>
                  <b-spinner v-if="docLoading" small />
                </div>
                <div class="row small">
                  <div class="col-md-4"><span class="text-muted">{{ $t('label.docNo') }}:</span> <span class="font-monospace">{{ selectedSalesDoc.doc_no }}</span></div>
                  <div class="col-md-4"><span class="text-muted">{{ $t('label.docDate') }}:</span> {{ selectedSalesDoc.doc_date || '—' }}</div>
                  <div class="col-md-4"><span class="text-muted">{{ $t('delivery.total') }}:</span> {{ selectedSalesDoc.total || pickedDoc.DocAmt || '—' }}</div>
                </div>
                <div v-if="pickedDocLines.length" class="mt-2">
                  <table class="table table-sm table-borderless small mb-0">
                    <thead class="text-muted">
                      <tr>
                        <th>{{ $t('label.itemCode') }}</th>
                        <th>{{ $t('label.description') }}</th>
                        <th class="text-right">{{ $t('label.qty') }}</th>
                        <th class="text-right">{{ $t('label.uom') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(ln, i) in pickedDocLines" :key="i">
                        <td class="font-monospace">{{ ln.ItemCode || ln.item_code }}</td>
                        <td>{{ ln.Description || ln.description }}</td>
                        <td class="text-right">{{ ln.Qty || ln.qty }}</td>
                        <td class="text-right">{{ ln.UOM || ln.uom }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </b-col>
          <b-col cols="12">
            <b-form-group :label="$t('delivery.addressUrl')">
              <b-form-input v-model="newForm.deliveryAddress" type="url" :placeholder="$t('delivery.addressUrlHint')" />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group :label="$t('delivery.notes')">
              <b-form-textarea v-model="newForm.notes" rows="2" />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-checkbox v-model="newForm.autoCreateDo">
              {{ $t('delivery.autoCreateDo') }}
            </b-form-checkbox>
          </b-col>
        </b-row>
        <div class="text-right mt-3">
          <b-button variant="light" class="mr-2" @click="$bvModal.hide('createDeliveryModal')">{{ $t('label.cancel') }}</b-button>
          <b-button type="submit" variant="primary" :disabled="!canSubmitNew || creating">
            <b-spinner v-if="creating" small class="mr-1"></b-spinner>
            {{ $t('label.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </Layout>
</template>
